import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@copro/api";
import { Database } from "@copro/api";
import { keys } from "./keys";
import { keys as documentKeys } from "../../documents/api/keys";
import { syncOwnedDocument } from "../../documents/api/syncOwnedDocument";
import { deleteDriveFilesQuietly } from "../../documents/api/driveFiles";
import { useSpace } from "../../../hooks/useSpace";

// file_url is not a contract column anymore: it lives in a linked document.
type Command = Database['public']['Tables']['provider_contracts']['Insert'] & {
  file_url?: string | null;
};

async function postProviderContract(command: Command, spaceId: string) {
  const { file_url, ...contract } = command;

  const documentId = await syncOwnedDocument({
    currentDocumentId: null,
    fileUrl: file_url ?? null,
    name: contract.name,
    type: 'Contrat',
    ownerColumn: 'provider_id',
    ownerId: contract.provider_id,
    spaceId,
  });

  const { data, error } = await apiClient
    .from('provider_contracts')
    .insert({ ...contract, document_id: documentId })
    .select()
    .single();

  if (error) {
    // Roll back the just-created file so it doesn't linger as an orphan document.
    if (documentId) {
      await deleteDriveFilesQuietly([file_url], spaceId);
      await apiClient.from('documents').delete().eq('id', documentId);
    }
    throw error;
  }

  return data;
}

type Variables = {
  providerId: string;
  command: Command;
}

export default function usePostProviderContract() {
  const queryClient = useQueryClient();
  const { currentSpace } = useSpace();

  return useMutation({
    mutationFn: ({ command }: Variables) => {
      if (!currentSpace) {
        throw new Error('Aucun espace sélectionné pour enregistrer le contrat.');
      }

      return postProviderContract(command, currentSpace.id);
    },
    onSuccess: (_, { providerId }) => {
      queryClient.invalidateQueries({ queryKey: keys.getProviderContracts(providerId) });
      // The contract file also lives in documents (unified list, provider list/count).
      queryClient.invalidateQueries({ queryKey: documentKeys.documents });
    },
  });
}
